/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Grid, IconButton, Tooltip, Typography } from '@material-ui/core';
import {
  MoreVert as MoreVertIcon,
  Done as DoneIcon,
  Close as CloseIcon,
  Replay as ReplayIcon,
} from '@material-ui/icons';

import moment from 'moment';

import { withContext } from 'common/helpers/context';
import { getStatusText } from 'common/helpers/getStatusText';
import { getPassportRowActions } from 'common/helpers/getPassportRowActions';
import { SortableTable } from 'common/components/SortableTable';
import PassportActionMenu from 'common/components/PassportActionMenu/PassportActionMenu';

import { submitPassportRequest } from '../../actions/submitPassportRequest';
import resource from './PassportRequestTabular.resource';
import { RequestStatusPill } from './components/RequestStatusPill';
import { getRandomCrn } from 'common/helpers/getRandomCrn';

const DATE_FORMAT = 'YYYY-MM-DD';

function createHeaders(isOutbound) {
  return [
    {
      id: 'crn',
      align: 'left',
      numeric: true,
      label: 'ID',
    },
    {
      id: 'name',
      align: 'left',
      numeric: false,
      label: 'Candidate',
    },
    {
      id: 'role',
      align: 'left',
      numeric: false,
      label: 'Role',
    },
    {
      id: 'discipline',
      align: 'left',
      numeric: false,
      label: 'Discipline',
    },
    {
      id: 'requestor',
      align: 'left',
      numeric: true,
      label: isOutbound ? 'Sponsor' : 'Requestor',
    },
    {
      id: 'status',
      align: 'center',
      numeric: false,
      label: 'Status',
    },
    {
      id: 'requestedAt',
      align: 'left',
      numeric: false,
      label: 'Requested',
    },
    {
      id: 'decidedAt',
      align: 'left',
      numeric: false,
      label: 'Decided',
    },
    {
      id: 'buttonGroup',
      align: 'left',
      numeric: false,
      label: '',
    },
  ];
}

function createData(
  item,
  isOutbound,
  handleClickAction,
  handleClickApprove,
  handleClickReject,
  handleClickReset,
) {
  const { candidate, requestor, feedback } = item;
  const {
    user,
    crn,
    role,
    discipline,
    assignment,
    id: candidateId,
  } = candidate;
  const { firstName, lastName } = user;

  const hasPermission = feedback === 1;

  // Distinguish the candidate's OWN request (requestor is the candidate) from a searcher
  // requesting someone else's passport. Only anonymise the latter, and only until approved —
  // a candidate should never see their own row masked as "Jane Doe" with a random ID.
  const isSelf = !!user && item.requestorId === user.id;
  const masked = isOutbound && !isSelf && !hasPermission;

  // Decided rows are reversible (approve/reject/reset-to-pending). See getPassportRowActions.
  // The row's current feedback is passed to each handler so a reversal that reduces access can
  // be confirmed before the request is sent.
  const { showApprove, showReject, showReset } = getPassportRowActions(
    feedback,
    isOutbound,
  );
  const buttonGroup = (
    <>
      {showApprove && (
        <Tooltip title="Approve">
          <IconButton
            size="small"
            color="primary"
            style={{ marginRight: '10px' }}
            onClick={handleClickApprove(candidateId, feedback)}
          >
            <DoneIcon />
          </IconButton>
        </Tooltip>
      )}
      {showReject && (
        <Tooltip title="Reject">
          <IconButton
            size="small"
            color="secondary"
            style={{ marginRight: '10px' }}
            onClick={handleClickReject(candidateId, feedback)}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      )}
      {showReset && (
        <Tooltip title="Reset to pending">
          <IconButton
            size="small"
            style={{ marginRight: '10px' }}
            onClick={handleClickReset(candidateId, feedback)}
          >
            <ReplayIcon />
          </IconButton>
        </Tooltip>
      )}
      <IconButton
        aria-controls="passport-action-menu"
        aria-haspopup="true"
        onClick={handleClickAction(item)}
      >
        <MoreVertIcon />
      </IconButton>
    </>
  );

  return [
    {
      id: 'crn',
      value: masked ? getRandomCrn() : crn,
      blur: masked,
    },
    {
      id: 'name',
      value: masked ? 'Jane Doe' : `${firstName} ${lastName}`,
      blur: masked,
    },
    {
      id: 'role',
      value: role.name,
    },
    {
      id: 'discipline',
      value: discipline.name,
    },
    {
      id: 'requestor',
      value: isOutbound
        ? `${assignment.token.sponsor.firstName} ${assignment.token.sponsor.lastName}`
        : `${requestor.firstName} ${requestor.lastName}`,
    },
    {
      id: 'status',
      value: <RequestStatusPill status={getStatusText(feedback)} />,
      align: 'center',
    },
    {
      id: 'requestedAt',
      value: item.createdAt ? moment(item.createdAt).format(DATE_FORMAT) : '-',
    },
    {
      // Undecided while pending; once approved/rejected, updated_at is the decision time.
      id: 'decidedAt',
      value:
        feedback === 0 || !item.updatedAt
          ? '-'
          : moment(item.updatedAt).format(DATE_FORMAT),
    },
    {
      id: 'buttonGroup',
      value: buttonGroup,
      align: 'right',
    },
  ];
}

const PassportRequestTabular = (props) => {
  const [anchorElAction, setAnchorElAction] = useState(null);
  const [selectedPassportRequest, setSelectedPassportRequest] = useState(null);
  const [rows, setRows] = useState([]);

  const { userInfo, isOutbound, title, passportRequests } = props;
  const { id: userId } = userInfo;

  useEffect(() => {
    setRows(
      passportRequests.map((e) =>
        createData(
          e,
          isOutbound,
          handleClickAction,
          handleClickApprove,
          handleClickReject,
          handleClickReset,
        ),
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passportRequests]);

  const handleClickApprove = (candidateId, fromFeedback) => () => {
    props.submitPassportRequest({ userId, candidateId, feedback: 1, fromFeedback });
  };

  const handleClickReject = (candidateId, fromFeedback) => () => {
    props.submitPassportRequest({ userId, candidateId, feedback: 2, fromFeedback });
  };

  const handleClickReset = (candidateId, fromFeedback) => () => {
    props.submitPassportRequest({ userId, candidateId, feedback: 0, fromFeedback });
  };

  const handleClickAction = (candidate) => (e) => {
    setSelectedPassportRequest(candidate);
    setAnchorElAction(e.currentTarget);
  };

  const handleActionClose = () => {
    setAnchorElAction(null);
  };

  return (
    <Grid item xs={12}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h5">{title}</Typography>
          <Typography variant="body1" color="textPrimary">
            {isOutbound
              ? resource.DESCRIPTIONS.OUTBOUND
              : resource.DESCRIPTIONS.INBOUND}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <SortableTable
            name={`${isOutbound ? 'outbound' : 'inbound'}-request-table`}
            headers={createHeaders(isOutbound)}
            rows={rows}
            defaultSortColumnIndex={1}
            searchable
          />
          <PassportActionMenu
            candidate={
              selectedPassportRequest && selectedPassportRequest.candidate
            }
            feedback={
              selectedPassportRequest && selectedPassportRequest.feedback
            }
            anchorEl={anchorElAction}
            onClose={handleActionClose}
            showShare={
              !isOutbound ||
              (selectedPassportRequest &&
                selectedPassportRequest.feedback === 1)
            }
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

PassportRequestTabular.propTypes = {
  isOutbound: PropTypes.bool,
  title: PropTypes.string.isRequired,
  passportRequests: PropTypes.array.isRequired,
  submitPassportRequest: PropTypes.func.isRequired,
};

PassportRequestTabular.defaultProps = {
  isOutbound: false,
};

export default withContext(
  connect(null, { submitPassportRequest })(PassportRequestTabular),
);
